import { useTranslation } from 'react-i18next'
import { Base } from '@itsrever/design-system'
import { SearchCatalogKindEnum } from '@itsrever/rever-api'
import { useCatalogItems } from '@/application/queries'
import { CatalogItem, CatalogKind } from '@/domain/catalog'
import {
  NonReturnableListKey,
  NonReturnableLists,
  changedNonReturnableLists,
  nonReturnableListsFor
} from '@/application/manage-return-conditions'
import { SaveBar } from '@/ui/shared'
import { CatalogCopyKey, CatalogPicker } from './catalog-picker'
import { FreeTextCopyKey, FreeTextListField } from './free-text-list-field'

type CatalogListKey = Exclude<NonReturnableListKey, 'orderTags'>

const freeTextCopyKeys: Record<NonReturnableListKey, FreeTextCopyKey> = {
  productIds: 'product_ids',
  collectionIds: 'collection_ids',
  productTypes: 'category_ids',
  productTags: 'tag_ids',
  orderTags: 'order_tags'
}

const pickers: {
  key: CatalogListKey
  kind: CatalogKind
  copyKey: CatalogCopyKey
}[] = [
  {
    key: 'productIds',
    kind: SearchCatalogKindEnum.Product,
    copyKey: 'products'
  },
  {
    key: 'collectionIds',
    kind: SearchCatalogKindEnum.Collection,
    copyKey: 'collections'
  },
  {
    key: 'productTypes',
    kind: SearchCatalogKindEnum.ProductType,
    copyKey: 'product_types'
  },
  {
    key: 'productTags',
    kind: SearchCatalogKindEnum.ProductTag,
    copyKey: 'product_tags'
  }
]

interface NonReturnableFormProps {
  ecommerceId?: string
  platform?: string
  lists: NonReturnableLists
  originalLists: NonReturnableLists
  setLists: (lists: NonReturnableLists) => void
  canEdit: boolean
  saving: boolean
  onSave: () => void
}

export const NonReturnableForm: React.FC<NonReturnableFormProps> = ({
  ecommerceId,
  platform,
  lists,
  originalLists,
  setLists,
  canEdit,
  saving,
  onSave
}) => {
  const { t } = useTranslation()
  // Only Shopify can search its catalog; elsewhere the lists are typed in and never resolved.
  const isShopify = platform === 'SHOPIFY'
  const editableLists = nonReturnableListsFor(platform)
  const savedIds = (key: CatalogListKey) =>
    isShopify ? originalLists[key] : []
  const resolved: Record<CatalogListKey, ReturnType<typeof useCatalogItems>> = {
    productIds: useCatalogItems(
      ecommerceId,
      SearchCatalogKindEnum.Product,
      savedIds('productIds')
    ),
    collectionIds: useCatalogItems(
      ecommerceId,
      SearchCatalogKindEnum.Collection,
      savedIds('collectionIds')
    ),
    productTypes: useCatalogItems(
      ecommerceId,
      SearchCatalogKindEnum.ProductType,
      savedIds('productTypes')
    ),
    productTags: useCatalogItems(
      ecommerceId,
      SearchCatalogKindEnum.ProductTag,
      savedIds('productTags')
    )
  }

  const knownOf = (key: CatalogListKey) =>
    new Map<string, CatalogItem>(
      resolved[key].items.map((item) => [item.id, item])
    )
  // Only saved rules can be stale: anything picked in this session came from the store.
  const staleIdsOf = (key: CatalogListKey) => {
    if (!isShopify || !resolved[key].resolved) return []
    const known = knownOf(key)
    return lists[key].filter(
      (id) => originalLists[key].includes(id) && !known.has(id)
    )
  }
  const staleCount = pickers.reduce(
    (count, { key }) => count + staleIdsOf(key).length,
    0
  )
  const changes = changedNonReturnableLists(originalLists, lists).length

  const set = (key: NonReturnableListKey) => (ids: string[]) =>
    setLists({ ...lists, [key]: ids })

  return (
    <div className="flex flex-col gap-4">
      {staleCount > 0 ? (
        <Base.Callout>
          <p className="text-sm font-medium">
            {t('non_returnable.stale_title', { count: staleCount })}
          </p>
          <p className="text-sm">{t('non_returnable.stale_description')}</p>
        </Base.Callout>
      ) : null}
      {isShopify ? (
        <>
          {pickers.map(({ key, kind, copyKey }) => (
            <CatalogPicker
              key={key}
              ecommerceId={ecommerceId}
              kind={kind}
              copyKey={copyKey}
              value={lists[key]}
              known={knownOf(key)}
              staleIds={staleIdsOf(key)}
              onChange={set(key)}
              canEdit={canEdit}
            />
          ))}
          <FreeTextListField
            copyKey="order_tags"
            value={lists.orderTags}
            onChange={set('orderTags')}
            canEdit={canEdit}
          />
        </>
      ) : (
        editableLists.map((key) => (
          <FreeTextListField
            key={key}
            copyKey={freeTextCopyKeys[key]}
            platform={platform}
            value={lists[key]}
            onChange={set(key)}
            canEdit={canEdit}
          />
        ))
      )}
      <SaveBar
        editable={canEdit}
        changes={changes}
        saving={saving}
        onDiscard={() => setLists(originalLists)}
        onSave={onSave}
      />
    </div>
  )
}
